import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  Icon,
  Label,
  MenuToggle,
  type MenuToggleElement,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Pagination,
  SearchInput,
  Select,
  SelectList,
  SelectOption,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { ActionsColumn, type IAction, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { ExclamationCircleIcon, KeyIcon, PlusCircleIcon, SearchIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import Unauthenticated from '@app/Unauthenticated';
import { NotFound } from '@app/NotFound/NotFound';
import { useUserContext } from '../../../contexts/user/UserContext';
import { useAdminCertificatePlatforms } from '../../../hooks/useAdminCertificatePlatforms';
import { formatDateTime } from '../../../utils/dateFormat';
import {
  type AdminCertificatePlatform,
  ZEROSSL_DEFAULT_BASE_URL,
  certificatePlatformTypeLabel,
} from '../../../types/models/AdminCertificatePlatform';
import CertificatePlatformModal from './CertificatePlatformModal';
import CertificatePlatformDeleteModal from './CertificatePlatformDeleteModal';

// Scope filter values: 'all', 'shared', or `client:<name>` for one dedicated client. The client
// options are derived from the fetched rows (not the full client list) — only names that actually
// own a platform are useful filters here.
const SCOPE_ALL = 'all';
const SCOPE_SHARED = 'shared';
const SCOPE_CLIENT_PREFIX = 'client:';

/**
 * Certificate Platform — an Ancalagon-staff-only Admin page managing the `certificate_platform`
 * catalog: the upstream CA/ACME endpoints (OPNsense Trust internal CAs, OPNsense ACME/Let's
 * Encrypt) the Manager syncs certificates from. Staff can create, edit, and delete platforms,
 * including the driver connection settings that live in the backend's `metadata` JSON (exploded
 * into individual form fields — raw JSON is never shown).
 *
 * NOT the tenant-facing Certificates area (src/app/Datacenter/Certificates/…), which consumes the
 * tenant-scoped, read-only /datacenters/certificate-platforms surface. This page consumes the
 * staff /admin/certificate-platforms surface, which returns a different, richer row shape.
 *
 * Gating is enforced in two independent places (defense in depth):
 *   1. AppLayout hides the whole "Admin" nav group unless `user.is_ancalagon_staff`.
 *   2. This page guards itself: a non-staff user who types `/admin/certificate-platforms` directly
 *      gets <NotFound /> — the same response as a non-existent route, so the admin area's
 *      existence is never leaked. (The backend `/admin` routes enforce this authoritatively;
 *      this is the frontend mirror.)
 */
function CertificatePlatforms() {
  const auth = useAuth();
  const user = useUserContext();
  const isStaff = user?.is_ancalagon_staff === true;
  // `isStaff` gates the fetch so a non-staff visitor (shown <NotFound /> below) never fires the
  // backend-rejected admin call.
  const { platforms, loading, error, refetch, applyPlatform, removePlatform } = useAdminCertificatePlatforms(isStaff);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AdminCertificatePlatform | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminCertificatePlatform | null>(null);

  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState<string>(SCOPE_ALL);
  const [isScopeOpen, setIsScopeOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchValue]);

  const clientNames = useMemo(() => {
    const names = new Set<string>();
    platforms.forEach((p) => {
      if (p.client_name !== null) {
        names.add(p.client_name);
      }
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [platforms]);

  const filteredPlatforms = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    return platforms.filter((p) => {
      if (scopeFilter === SCOPE_SHARED && p.clientID !== null) {
        return false;
      }
      if (scopeFilter.startsWith(SCOPE_CLIENT_PREFIX) && p.client_name !== scopeFilter.slice(SCOPE_CLIENT_PREFIX.length)) {
        return false;
      }
      if (!q) {
        return true;
      }
      return (
        p.name.toLowerCase().includes(q) ||
        (p.metadata.base_url ?? '').toLowerCase().includes(q) ||
        certificatePlatformTypeLabel(p.type).toLowerCase().includes(q)
      );
    });
  }, [platforms, debouncedSearch, scopeFilter]);

  const paginatedPlatforms = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredPlatforms.slice(start, start + perPage);
  }, [filteredPlatforms, page, perPage]);

  // Deleting the last row of the last page (or shrinking the filtered set) can leave `page`
  // pointing past the end — clamp it back so the table never renders an empty page.
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredPlatforms.length / perPage));
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredPlatforms.length, perPage, page]);

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  // Ancalagon-staff-only. Treat anything other than an explicit `true` as "no access" and
  // render the 404 page so the route reveals nothing about the admin area's existence.
  if (!isStaff) {
    return <NotFound />;
  }

  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, pageNumber: number) => {
    setPage(pageNumber);
  };

  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const clearFilters = () => {
    setSearchValue('');
    // Also clear the debounced value directly so the table recovers immediately instead of after
    // the 400ms debounce tick.
    setDebouncedSearch('');
    setScopeFilter(SCOPE_ALL);
    setPage(1);
  };

  const scopeFilterLabel =
    scopeFilter === SCOPE_ALL
      ? 'All scopes'
      : scopeFilter === SCOPE_SHARED
        ? 'Shared'
        : scopeFilter.slice(SCOPE_CLIENT_PREFIX.length);

  const rowActions = (platform: AdminCertificatePlatform): IAction[] => [
    {
      title: 'Edit',
      onClick: () => setEditTarget(platform),
    },
    { isSeparator: true },
    {
      title: 'Delete',
      isDanger: true,
      onClick: () => setDeleteTarget(platform),
    },
  ];

  const renderContent = () => {
    if (loading) {
      return <CenteredSpinner />;
    }

    if (error) {
      return (
        <EmptyState
          titleText="Unable to load certificate platforms"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (platforms.length === 0) {
      return (
        <EmptyState titleText="No certificate platforms yet" icon={KeyIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            Add the first upstream CA or ACME endpoint so the Manager can start syncing certificates from it.
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => setIsCreateOpen(true)}>
                Add certificate platform
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    return (
      <>
        <Toolbar>
          <ToolbarContent>
            <ToolbarItem>
              <SearchInput
                placeholder="Search by name, endpoint or type..."
                value={searchValue}
                onChange={(_event, value) => setSearchValue(value)}
                onClear={() => setSearchValue('')}
              />
            </ToolbarItem>
            <ToolbarItem>
              <Select
                id="platform-scope-filter"
                isOpen={isScopeOpen}
                selected={scopeFilter}
                onSelect={(_event, value) => {
                  setScopeFilter(String(value));
                  setPage(1);
                  setIsScopeOpen(false);
                }}
                onOpenChange={(open) => setIsScopeOpen(open)}
                toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                  <MenuToggle
                    ref={toggleRef}
                    onClick={() => setIsScopeOpen((open) => !open)}
                    isExpanded={isScopeOpen}
                    aria-label="Filter by scope"
                  >
                    {scopeFilterLabel}
                  </MenuToggle>
                )}
                shouldFocusToggleOnSelect
              >
                <SelectList>
                  <SelectOption value={SCOPE_ALL}>All scopes</SelectOption>
                  <SelectOption value={SCOPE_SHARED}>Shared</SelectOption>
                  {clientNames.map((name) => (
                    <SelectOption key={name} value={`${SCOPE_CLIENT_PREFIX}${name}`}>
                      {name}
                    </SelectOption>
                  ))}
                </SelectList>
              </Select>
            </ToolbarItem>
            <ToolbarItem variant="pagination">
              <Pagination
                itemCount={filteredPlatforms.length}
                perPage={perPage}
                page={page}
                onSetPage={onSetPage}
                onPerPageSelect={onPerPageSelect}
              />
            </ToolbarItem>
          </ToolbarContent>
        </Toolbar>

        {filteredPlatforms.length === 0 ? (
          <EmptyState
            titleText="No certificate platforms match your filters"
            icon={SearchIcon}
            variant="lg"
            headingLevel="h2"
          >
            <EmptyStateBody>
              Try a different search term or scope, or clear the filters to see all certificate platforms.
            </EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>
                <Button variant="primary" onClick={clearFilters}>
                  Clear filters
                </Button>
              </EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        ) : (
          <Table aria-label="Certificate platforms list" variant="compact">
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>Type</Th>
                <Th>Scope</Th>
                <Th>Endpoint</Th>
                <Th>Certificates</Th>
                <Th>Last synced</Th>
                <Th screenReaderText="Row actions" />
              </Tr>
            </Thead>
            <Tbody>
              {paginatedPlatforms.map((platform) => (
                <Tr key={platform.id}>
                  <Td dataLabel="Name">
                    {platform.name}
                    {!platform.has_api_credentials && (
                      <>
                        {' '}
                        <Label status="warning" isCompact>
                          No credentials
                        </Label>
                      </>
                    )}
                    {/* The Trust driver cannot call the platform without an issuing-CA UUID — flag
                        legacy rows missing one, same as missing credentials. (ACME ignores it.) */}
                    {platform.type === 'opnsense-api-trust' && platform.metadata.ca_uuid === null && (
                      <>
                        {' '}
                        <Label status="warning" isCompact>
                          No CA UUID
                        </Label>
                      </>
                    )}
                  </Td>
                  <Td dataLabel="Type">{certificatePlatformTypeLabel(platform.type)}</Td>
                  <Td dataLabel="Scope">
                    {platform.clientID === null ? (
                      <Label color="blue" isCompact>
                        Shared
                      </Label>
                    ) : (
                      platform.client_name
                    )}
                  </Td>
                  {/* A zerossl row with no stored base_url runs against the driver default — show
                      that instead of an em-dash (which is reserved for malformed legacy rows). */}
                  <Td dataLabel="Endpoint">
                    {platform.metadata.base_url ?? (platform.type === 'zerossl' ? ZEROSSL_DEFAULT_BASE_URL : '—')}
                  </Td>
                  <Td dataLabel="Certificates">{platform.certificate_count}</Td>
                  <Td dataLabel="Last synced">
                    {platform.last_synced_at === null ? 'Never' : formatDateTime(platform.last_synced_at)}
                  </Td>
                  <Td isActionCell>
                    <ActionsColumn items={rowActions(platform)} />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}

        {filteredPlatforms.length > 0 && (
          <Pagination
            itemCount={filteredPlatforms.length}
            perPage={perPage}
            page={page}
            onSetPage={onSetPage}
            onPerPageSelect={onPerPageSelect}
            variant="bottom"
          />
        )}
      </>
    );
  };

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem>Admin</BreadcrumbItem>
          <BreadcrumbItem isActive>Certificate Platform</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Flex justifyContent={{ default: 'justifyContentSpaceBetween' }} alignItems={{ default: 'alignItemsFlexStart' }}>
          <FlexItem>
            <Content component="h1">
              <Icon size="lg" style={{ marginRight: '8px' }}>
                <KeyIcon />
              </Icon>
              Certificate Platform
            </Content>
            <Content component="p">
              The upstream CA and ACME endpoints the Manager syncs certificates from. Shared platforms are visible to
              every tenant; dedicated platforms belong to a single client.
            </Content>
          </FlexItem>
          {!loading && (
            <FlexItem>
              <Button variant="primary" icon={<PlusCircleIcon />} onClick={() => setIsCreateOpen(true)}>
                Add certificate platform
              </Button>
            </FlexItem>
          )}
        </Flex>
      </PageSection>
      <PageSection hasBodyWrapper={false}>{renderContent()}</PageSection>

      {/* Mounted only while open: each open starts from a fresh mount (clean form state), and the
          modal's client-list fetch (useClients) doesn't fire until it's needed. */}
      {(isCreateOpen || editTarget !== null) && (
        <CertificatePlatformModal
          platform={editTarget}
          onClose={() => {
            setIsCreateOpen(false);
            setEditTarget(null);
          }}
          onSaved={applyPlatform}
        />
      )}
      {deleteTarget !== null && (
        <CertificatePlatformDeleteModal
          platform={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={removePlatform}
        />
      )}
    </>
  );
}

export { CertificatePlatforms };
